import { useState } from "react";
import { useBilling } from "../context/BillingContext";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";

const FEATURES = [
  "Runway & burn rate tracking",
  "Departments",
  "Categories",
  "Spending groups",
  "Team members & roles",
  "CSV import",
  "Investor share link",
  "Priority support",
];

export default function Billing() {
  const { subscriptionStatus, subscriptionInterval, periodEnd, isPro, isTrialing, trialDaysLeft, openPortal } = useBilling();
  const [interval, setInterval] = useState("month");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const MONTHLY = 50;
  const YEARLY  = 500;

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;

  const handleUpgrade = async () => {
    setError(""); setLoading(true);
    try {
      const fns = getFunctions(getApp(), "us-central1");
      const createCheckout = httpsCallable(fns, "createCheckoutSession");
      const result = await createCheckout({ interval });
      if (result.data?.url) { window.location.href = result.data.url; }
      else throw new Error("No URL returned");
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong.");
      setLoading(false);
    }
  };

  const handleManage = async () => {
    setLoading(true);
    try { await openPortal(); }
    catch (err) { setError("Could not open portal."); setLoading(false); }
  };

  return (
    <div style={{ maxWidth: 560 }}>
      <div className="page-header">
        <div>
          <div className="page-title">Billing</div>
          <div className="page-subtitle">$ manage your subscription</div>
        </div>
      </div>

      {/* Current plan */}
      <div className={`card ${isPro ? "hover-green" : ""}`} style={{ marginBottom: 8, borderColor: isPro ? "var(--green-dim)" : "var(--border)" }}>
        <div className="label" style={{ marginBottom: 8 }}>CURRENT_PLAN</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <span className="mono" style={{ fontSize: 16, color: isPro ? "var(--green)" : "#555", fontWeight: 500 }}>
              {isPro ? "greenline pro" : "free"}
            </span>
            {isTrialing && (
              <span className="badge badge-amber" style={{ marginLeft: 10 }}>trial</span>
            )}
            {isPro && !isTrialing && subscriptionInterval && (
              <span className="badge badge-green" style={{ marginLeft: 10 }}>{subscriptionInterval}ly</span>
            )}
            {isTrialing && (
              <div className="mono" style={{ fontSize: 10, color: "#f59e0b", marginTop: 4 }}>
                {trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""} left in trial
              </div>
            )}
            {isPro && !isTrialing && periodEnd && (
              <div className="mono" style={{ fontSize: 10, color: "#666", marginTop: 4 }}>
                {subscriptionStatus === "canceled" ? "access until" : "renews"} {formatDate(periodEnd)?.toLowerCase()}
              </div>
            )}
          </div>
          {isPro && (
            <button className="btn" onClick={handleManage} disabled={loading} style={{ fontSize: 10 }}>
              {loading ? "loading..." : "manage →"}
            </button>
          )}
        </div>
      </div>

      {/* Upgrade */}
      {!isPro && (
        <div className="card hover-green" style={{ borderColor: "var(--green-dim)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
            <div>
              <div className="label" style={{ marginBottom: 4 }}>GREENLINE_PRO</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
                <span className="mono" style={{ fontSize: 24, color: "var(--text)", fontWeight: 500 }}>
                  ${interval === "year" ? Math.round(YEARLY / 12) : MONTHLY}
                </span>
                <span className="mono" style={{ fontSize: 11, color: "#666" }}>/ month</span>
              </div>
              {interval === "year" && (
                <div className="mono" style={{ fontSize: 10, color: "var(--green)", marginTop: 2 }}>
                  ${YEARLY}/yr — save ${MONTHLY * 12 - YEARLY}
                </div>
              )}
            </div>

            {/* Toggle */}
            <div style={{ display: "flex", background: "#050505", border: "1px solid #111", borderRadius: 6, padding: 3, gap: 3 }}>
              {["month", "year"].map(i => (
                <button key={i} onClick={() => setInterval(i)} style={{
                  background: interval === i ? "var(--green)" : "transparent",
                  color: interval === i ? "#000" : "#333",
                  border: "none", borderRadius: 4, padding: "4px 10px",
                  cursor: "pointer", fontSize: 10, fontFamily: "var(--mono)",
                  fontWeight: interval === i ? 600 : 400, transition: "all 0.15s",
                }}>
                  {i}ly{i === "year" && <span style={{ marginLeft: 4, color: interval === "year" ? "#000" : "var(--green)", fontSize: 9 }}>-17%</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Features */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px", marginBottom: 14 }}>
            {FEATURES.map(f => (
              <div key={f} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ color: "var(--green)", fontSize: 10 }}>✓</span>
                <span className="mono" style={{ fontSize: 10, color: "#555" }}>{f.toLowerCase()}</span>
              </div>
            ))}
          </div>

          {error && <div className="badge badge-red" style={{ display: "block", marginBottom: 8 }}>{error}</div>}

          <button className="btn btn-green" onClick={handleUpgrade} disabled={loading} style={{ width: "100%", fontSize: 12 }}>
            {loading ? "redirecting to stripe..." : `upgrade — $${interval === "year" ? YEARLY + "/yr" : MONTHLY + "/mo"}`}
          </button>
          <div className="mono" style={{ fontSize: 9, color: "#555", textAlign: "center", marginTop: 6 }}>
            secure via stripe — cancel anytime
          </div>
        </div>
      )}
    </div>
  );
}